import { throwErrorMessage } from 'dashboard/store/utils/api';
import ConversationApi from '../../../../api/inbox/conversation';
import mutationTypes from '../../../mutation-types';

// Both read states come back from the server: marking read as a non-assignee
// must leave the assignee's badge as it is.
const readStateOf = ({
  id,
  agent_last_seen_at,
  assignee_last_seen_at,
  unread_count,
  assignee_unread_count,
}) => ({
  id,
  agent_last_seen_at,
  assignee_last_seen_at,
  unread_count,
  assignee_unread_count,
});

const READ_STAMPS = ['agent_last_seen_at', 'assignee_last_seen_at'];

const newestMessageId = messages =>
  (messages || []).reduce((max, { id }) => (id > max ? id : max), 0);

// The response to "mark as read" is committed 4 s late (see below). By then the
// store may already hold something newer than what the server counted:
// - a message the response did not see. Ids, not the second-precision
//   `timestamp`: a message landing in the same second as the read would slip
//   past a time comparison. The response carries its newest message (the seed).
// - a newer read stamp (another read already landed over the cable).
// Either way the counts in the store are the fresher ones.
const isOutdated = (chat, response) => {
  if (!chat) return false;
  const seenByResponse = newestMessageId(response.messages);
  if (newestMessageId(chat.messages) > seenByResponse) return true;
  if (chat.timestamp > response.timestamp) return true;
  return READ_STAMPS.some(stamp => (chat[stamp] || 0) > (response[stamp] || 0));
};

// Read stamps only move forward: a late response never rewinds a newer read,
// which would put the unread divider and the jump back on messages the counts
// already consider read.
const forwardStamps = (chat, response) =>
  READ_STAMPS.reduce((stamps, stamp) => {
    stamps[stamp] = Math.max(chat?.[stamp] || 0, response[stamp] || 0);
    return stamps;
  }, {});

export default {
  markMessagesRead: async ({ commit, getters }, data) => {
    try {
      const response = await ConversationApi.markMessageRead(data);
      setTimeout(() => {
        const { id, unread_count, assignee_unread_count } = readStateOf(
          response.data
        );
        const chat = getters.getConversationById(id);
        const stamps = { id, ...forwardStamps(chat, response.data) };
        commit(
          mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT,
          isOutdated(chat, response.data)
            ? stamps
            : { ...stamps, unread_count, assignee_unread_count }
        );
      }, 4000);
    } catch (error) {
      // Handle error
    }
  },

  markMessagesUnread: async ({ commit }, { id }) => {
    try {
      const response = await ConversationApi.markMessagesUnread({ id });
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
        ...readStateOf(response.data),
        id,
      });
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};
